/**
 * 上传视频和图片
 */
import React, {useState, useRef, useEffect} from "react";
import {Upload, message, Modal} from "antd";
import {PlusOutlined, LoadingOutlined, PlayCircleOutlined, DeleteOutlined, CustomerServiceOutlined, FileOutlined} from '@ant-design/icons';
import localStorageUtil from '@/utils/localStorageUtil';
import ImagePreviewModal from '@/components/imagePreviewModal';
import VideoPreviewModal from '@/components/videoPreviewModal';
import styles from './index.module.scss';

interface UploadImageVideoProps {
  businessName: string; // 使用组件的业务名称
  uploadFilesChange: Function; // 上传文件改变之后的回调方法
  defaultUploadFiles?: Array<{ key: string, preview: string, contentType: string }>; // 默认已经上传成功的文件信息(编辑时使用)
  canUploadVideo?: boolean; // 是否支持上传视频
  limitSize?: number; // 上传内容大小限制(单位M)
  maxCount?: number; // 最多可以上传的个数
  multiple?: boolean; // 是否支持一次上传多张
  acceptTypes?: string; // 自定义可接受的文件类型，如 ".mp3,.mp4,.jpg,.awb,.avi"
}

const UploadImageVideo = (props: UploadImageVideoProps) => {
  const {businessName, uploadFilesChange, defaultUploadFiles, canUploadVideo, limitSize, maxCount, multiple, acceptTypes} = props;
  const uploadCountRef = useRef(0);
  const [uploadSuccessList, setUploadSuccessList] = useState<any>(defaultUploadFiles || []); // 上传成功的资源信息
  const [uploadLoading, setUploadLoading] = useState<boolean>(false); // 是否上传中
  const [previewUrl, setPreviewUrl] = useState<string>('') // 预览照片的url
  const [previewIdx, setPreviewIdx] = useState<number>(0) // 预览照片的idx(上下翻页用)
  const [videoUrl, setVideoUrl] = useState<string>('') // 视频播放的url
  const [audioUrl, setAudioUrl] = useState<string>('') // 音频播放的url

  // 有初始值时调用uploadFilesChange方法传递给使用的组件 
  useEffect(() => {
    if (defaultUploadFiles) {
      uploadFilesChange(defaultUploadFiles);
    }
  }, []);

  // 上传前限制条件(文件类型、文件大小)
  const beforeUpload = (file: any) => {
    let isLimitSize = true;
    if (limitSize) {
      isLimitSize = file.size / 1024 / 1024 < limitSize;
    }
    if (!isLimitSize) {
      message.error(`请上传${limitSize}M以内资源`);
    }
    
    // 如果有自定义的 acceptTypes，则使用自定义验证
    if (acceptTypes) {
      const fileName = file.name?.toLowerCase() || '';
      const acceptList = acceptTypes.toLowerCase().split(',').map((t: string) => t.trim());
      const isLimitType = acceptList.some((ext: string) => {
        if (ext.startsWith('.')) {
          return fileName.endsWith(ext);
        }
        // 支持 MIME 类型格式如 image/*, video/*
        if (ext.includes('*')) {
          const mimePrefix = ext.replace('/*', '');
          return file.type?.startsWith(mimePrefix);
        }
        return file.type === ext;
      });
      if (!isLimitType) {
        message.error(`请上传指定格式的文件`);
      }
      return isLimitSize && isLimitType;
    }
    
    const allowType = ['image'];
    if (canUploadVideo) {
      allowType.push('video');
    }
    const isLimitType = Boolean(allowType.find((item: string) => file.type?.startsWith(item)));
    if (!isLimitType) {
      message.error(canUploadVideo ? '请上传图片或视频' : '请上传图片');
    }
    return isLimitSize && isLimitType;
  };
  // 上传资源改变的方法
  const onUploadChange = (info: any) => {
    // 上传结束的计数+1
    const addUploadCount = () => {
      uploadCountRef.current += 1;
      if (uploadCountRef.current === (info.fileList || []).length) {
        setUploadLoading(false);
      }
    }
    if (info.file.status === 'uploading') {
      setUploadLoading(true);
    }
    if (info.file.status === 'error') {
      addUploadCount();
      message.error('上传失败');
    }
    if (info.file.status === 'done') {
      addUploadCount();
      const code = info.file.response?.code;
      console.log('code', code);
      if (code === 0) {
        const newUploadSuccessList = [...uploadSuccessList, info.file.response.data];
        setUploadSuccessList(newUploadSuccessList);
        uploadFilesChange(newUploadSuccessList);
      } else {
        message.error(info.file.response?.msg || '上传失败');
      }
    }
  }

  // 删除上传的内容
  const deleteUpload = (index: number, e: any) => {
    e.stopPropagation();
    const newUploadSuccessList = [...uploadSuccessList];
    newUploadSuccessList.splice(index, 1);
    setUploadSuccessList(newUploadSuccessList);
    uploadFilesChange(newUploadSuccessList);
  }

  // 预览
  const onPreview = (file: any) => {
    console.log(file, 'filefileflie')
    const fileUrl = file?.preview || file?.url;
    const fileType = getFileType(file.contentType);
    
    switch (fileType) {
      case 'image':
        setPreviewUrl(fileUrl);
        const idx = uploadSuccessList.findIndex((item: any) => item.key === file.key);
        console.log('idx', idx);
        setPreviewIdx(idx);
        break;
      case 'video':
        setVideoUrl(fileUrl);
        break;
      case 'audio':
        setAudioUrl(fileUrl);
        break;
      default:
        message.info('该文件类型暂不支持预览');
        break;
    }
  };

  // 获取文件类型
  const getFileType = (contentType: string): 'image' | 'video' | 'audio' | 'unknown' => {
    if (contentType?.startsWith('image')) return 'image';
    if (contentType?.startsWith('video')) return 'video';
    if (contentType?.startsWith('audio')) return 'audio';
    return 'unknown';
  };

  // 根据 contentType 渲染预览内容
  const renderPreviewContent = (item: any) => {
    const fileType = getFileType(item.contentType);
    const fileUrl = item.preview || item.url;
    
    switch (fileType) {
      case 'image':
        return <img src={fileUrl} alt={`${businessName}图片`} />;
      case 'video':
        return (
          <div className={styles.filePreview}>
            <PlayCircleOutlined />
            <span className={styles.fileLabel}>视频</span>
          </div>
        );
      case 'audio':
        return (
          <div className={styles.filePreview}>
            <CustomerServiceOutlined />
            <span className={styles.fileLabel}>音频</span>
          </div>
        );
      default:
        return (
          <div className={styles.filePreview}>
            <FileOutlined />
            <span className={styles.fileLabel}>文件</span>
          </div>
        );
    }
  };

  let accept = "image/*"; // upload的accept
  let uploadText = '上传图片'; // 上传的文案描述
  if (acceptTypes) {
    accept = acceptTypes;
    uploadText = '上传文件';
  } else if (canUploadVideo) {
    accept = "image/*,video/*";
    uploadText = '上传图片/视频';
  }

  return (
    <div className={styles.uploadImageVideo}>
      <div className={styles.previewBox}>
        {uploadSuccessList.map((item: any, index: number) => (
          <div className={styles.previewItem} key={item.key} onClick={() => onPreview(item)}>
            {renderPreviewContent(item)}
            <DeleteOutlined className={styles.deleteIcon} onClick={(e: any) => deleteUpload(index, e)}/>
          </div>
        ))}
      </div>
      {/* 没有限制上传数量或者上传数小于限制时显示上传 */}
      {(!maxCount || uploadSuccessList.length < maxCount) && <Upload
        accept={accept}
        action="/api/v2/resource/upload"
        headers={{access_token: localStorageUtil.getItem('access_token')}}
        beforeUpload={beforeUpload}
        onChange={onUploadChange}
        multiple={multiple}
      >
        <div className={styles.uploadCard}>
          {uploadLoading ? <LoadingOutlined/> : <PlusOutlined/>}
          <div>{uploadText}</div>
        </div>
      </Upload>}
      {/* 预览照片的弹窗 */}
      {Boolean(previewUrl) && <ImagePreviewModal
        url={previewUrl}
        fileName={`${businessName}图片`}
        pubArray={uploadSuccessList.filter((item: any) => item.contentType?.startsWith('image'))}
        pubIndex={previewIdx}
        closeModal={() => setPreviewUrl('')}
        showDownload={false}
      />}
      {/* 视频播放的弹窗 */}
      {Boolean(videoUrl) && <VideoPreviewModal
        url={videoUrl}
        fileName={`${businessName}视频`}
        closeModal={() => setVideoUrl('')}
        showDownload={false}
      />}
      {/* 音频播放的弹窗 */}
      <Modal
        title={`${businessName}音频`}
        visible={Boolean(audioUrl)}
        onCancel={() => setAudioUrl('')}
        footer={null}
        centered
        destroyOnClose
      >
        <audio controls autoPlay style={{ width: '100%' }}>
          <source src={audioUrl} />
          您的浏览器不支持音频播放
        </audio>
      </Modal>
    </div>
  )
}

export default UploadImageVideo;